import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { formatCurrency } from "../lib/utils";

/**
 * Popovers NÃO modais da lista de leads: período personalizado e faixa de
 * preço. Fecham por Escape e clique fora sem prender foco, de propósito — por
 * isso vivem separados da página, que hospeda o acesso rápido modal
 * (`src/lib/overlay-contract.test.ts`).
 */

/** "R$2.428" como no protótipo: sem espaço depois do símbolo. */
export function money(value: number): string {
  return formatCurrency(value).replace(/\s/g, "");
}

// --- período personalizado --------------------------------------------------

export function CustomPeriod({
  from,
  to,
  onApply,
  onClose,
}: {
  from: string | null;
  to: string | null;
  onApply: (from: string | null, to: string | null) => void;
  onClose: () => void;
}) {
  const [draftFrom, setDraftFrom] = useState(from ?? "");
  const [draftTo, setDraftTo] = useState(to ?? "");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (ref.current?.contains(target) || target.closest(".chips")) return;
      onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [onClose]);

  return (
    <div ref={ref} className="custom-pop" role="group" aria-label="Período personalizado">
      <label>
        De
        <input type="date" aria-label="Data inicial" value={draftFrom} onChange={(event) => setDraftFrom(event.target.value)} />
      </label>
      <label>
        Até
        <input type="date" aria-label="Data final" value={draftTo} onChange={(event) => setDraftTo(event.target.value)} />
      </label>
      <button
        type="button"
        className="btn-primary"
        disabled={!draftFrom && !draftTo}
        onClick={() => onApply(draftFrom || null, draftTo || null)}
      >
        Aplicar
      </button>
    </div>
  );
}

// --- faixa de preço ---------------------------------------------------------

export function PricePopup({
  anchorRef,
  bound,
  min,
  max,
  onChange,
  onClear,
  onClose,
}: {
  anchorRef: { current: HTMLButtonElement | null };
  bound: number;
  min: number;
  max: number;
  onChange: (min: number, max: number) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<CSSProperties>({});
  const [draft, setDraft] = useState({ min: Math.min(min, bound), max: Math.min(max, bound) });
  const step = Math.max(1, Math.round(bound / 100));

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setPosition({
      left: Math.max(12, Math.min(rect.left, window.innerWidth - 332)),
      top: rect.bottom + 8,
    });
    ref.current?.querySelector<HTMLElement>('[role="slider"]')?.focus();
  }, [anchorRef]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
      anchorRef.current?.focus();
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (ref.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [anchorRef, onClose]);

  const commit = (next: { min: number; max: number }) => {
    const clean = {
      min: Math.max(0, Math.min(next.min, next.max)),
      max: Math.min(bound, Math.max(next.max, next.min)),
    };
    setDraft(clean);
    onChange(clean.min, clean.max);
  };

  const valueAt = (clientX: number) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.round((ratio * bound) / step) * step;
  };

  const drag = (thumb: "min" | "max") => (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    const move = (moveEvent: PointerEvent) => {
      const value = valueAt(moveEvent.clientX);
      setDraft((current) =>
        thumb === "min"
          ? { ...current, min: Math.min(value, current.max) }
          : { ...current, max: Math.max(value, current.min) },
      );
    };
    const up = (upEvent: PointerEvent) => {
      handle.releasePointerCapture(upEvent.pointerId);
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", up);
      setDraft((current) => {
        onChange(current.min, current.max);
        return current;
      });
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", up);
  };

  const keys = (thumb: "min" | "max") => (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    const current = draft[thumb];
    let next: number | null = null;
    if (event.key === "ArrowRight" || event.key === "ArrowUp") next = current + step;
    if (event.key === "ArrowLeft" || event.key === "ArrowDown") next = current - step;
    if (event.key === "PageUp") next = current + step * 10;
    if (event.key === "PageDown") next = current - step * 10;
    if (event.key === "Home") next = thumb === "min" ? 0 : draft.min;
    if (event.key === "End") next = thumb === "max" ? bound : draft.max;
    if (next === null) return;
    event.preventDefault();
    commit(thumb === "min" ? { min: next, max: draft.max } : { min: draft.min, max: next });
  };

  const low = `${(draft.min / bound) * 100}%`;
  const high = `${(draft.max / bound) * 100}%`;

  return (
    <div ref={ref} className="z-price-popup" role="dialog" aria-label="Faixa de preço" style={position}>
      <div className="z-price-values">
        <span>
          <small>Mínimo</small>
          <output className="num">{money(draft.min)}</output>
        </span>
        <span>
          <small>Máximo</small>
          <output className="num">{money(draft.max)}</output>
        </span>
      </div>
      <div
        ref={trackRef}
        className="z-price-track"
        style={{ "--price-low": low, "--price-high": high } as CSSProperties}
        onPointerDown={(event) => {
          if ((event.target as HTMLElement).closest(".z-price-handle")) return;
          const value = valueAt(event.clientX);
          const nearest = Math.abs(value - draft.min) <= Math.abs(value - draft.max) ? "min" : "max";
          commit(nearest === "min" ? { min: value, max: draft.max } : { min: draft.min, max: value });
        }}
      >
        <span className="z-price-fill" aria-hidden="true" />
        {(["min", "max"] as const).map((thumb) => (
          <button
            key={thumb}
            type="button"
            className="z-price-handle"
            role="slider"
            aria-label={thumb === "min" ? "Valor mínimo" : "Valor máximo"}
            aria-orientation="horizontal"
            aria-valuemin={0}
            aria-valuemax={bound}
            aria-valuenow={draft[thumb]}
            aria-valuetext={money(draft[thumb])}
            style={{ "--handle": thumb === "min" ? low : high } as CSSProperties}
            onPointerDown={drag(thumb)}
            onKeyDown={keys(thumb)}
          />
        ))}
      </div>
      <div className="z-price-foot">
        <span>Filtra enquanto você ajusta</span>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => {
            setDraft({ min: 0, max: bound });
            onClear();
          }}
        >
          Limpar
        </button>
      </div>
    </div>
  );
}
